// 🐛 ช่องว่างใต้แผนที่เมื่อเปิดผ่าน SFSafariViewController (in-app browser จาก universal link/QR
// scan) — ลองแก้มาแล้ว 3 รอบที่ "ขนาด/การวัดความสูงของ DOM ฝั่งเรา" (CSS dvh/svh, JS
// window.innerHeight ใน useViewportHeight.js, ResizeObserver สั่ง resize แผนที่ใน ViewerMap.js) ไม่หาย
// เพราะ remote debug จริง (2026-09-07) วัดแล้ว DOM ของเราถูกต้องสมบูรณ์ 100% ตั้งแต่แรก (shell/map/gmap
// div สูงตรงกับ window.innerHeight เป๊ะ ไม่มีช่องว่างในเลย์เอาต์เลย) — ช่องว่างที่เห็นจริงๆ คือ **toolbar
// ล่างของ SFSafariViewController เอง** (native UI ของ Apple ไม่ใช่ของเว็บเรา — user ยืนยันแตะไม่ติดตรงนั้น)
// ที่ค้างโชว์ทับเนื้อหาส่วนล่างอยู่ ไม่ยุบตัวเอง
//
// เหมือน Safari ปกติ toolbar นี้ยุบก็ต่อเมื่อมี scroll gesture เกิดขึ้นจริงบน document — หน้านี้ทั้งหน้าถูก
// ตั้งใจทำให้ scroll ไม่ได้ (`.share-viewer-shell{overflow:hidden}`, `overscroll-behavior:none` กัน
// pull-to-refresh) จึงไม่มีโอกาสให้ toolbar ยุบเองเลยจนกว่า user จะบังเอิญลากบนพื้นที่ที่ไม่ถูก Google Maps
// ยึด touch ไปทำ pan/zoom (สอดคล้องกับที่ user รายงานไว้ก่อนหน้า — ลากแถบขาวแล้วหาย)
//
// แก้ด้วยเทคนิคคลาสสิกของ mobile web (`window.scrollTo(0,1)` เพื่อ nudge scroll 1px) — nudge เอง แทนรอ
// user ลากมือ เป็น scroll จริงของ native UIScrollView ที่รองรับ WKWebView (ไม่ใช่แค่ synthetic event) จึง
// trigger กลไกยุบ toolbar ของ WebKit ได้เหมือนที่ user ลากมือเอง (ยังไม่มี real iOS device verify)
//
// `body{height:100%}` (public/css/index.css) เป็น % เทียบ `<html>` ซึ่งทั้งแอปไม่เคยตั้ง height ให้เลย
// (`auto` เสมอ) — % ของ body จึงไม่ resolve เป็นค่าจริง `calc(100% + 1px)` บน body อย่างเดียวจึงไม่มีผล
// อะไร ต้องตั้ง height ให้ `<html>` เป็นค่าจริงก่อน (% ของ root element เทียบ initial containing block =
// viewport เสมอตาม spec ไม่ต้องพึ่ง ancestor) ถึงจะทำให้ % ของ body resolve ต่อได้จริง
//
// the white gap under the map when opened through SFSafariViewController (the in-app browser a
// universal link/QR scan opens) — already tried fixing it 3 times at "the size/measurement of our own
// DOM" (CSS dvh/svh, JS window.innerHeight in useViewportHeight.js, a ResizeObserver forcing a map
// resize in ViewerMap.js) and it never went away, because real remote debugging (2026-09-07) measured
// our DOM as 100% correct from the start (shell/map/gmap div heights exactly match window.innerHeight
// — no gap in the layout at all). The gap the viewer actually sees is **SFSafariViewController's own
// bottom toolbar** (Apple's native UI, not our page — the user confirmed it doesn't respond to taps)
// sitting on top of the lower part of the content, refusing to auto-collapse.
//
// Like regular Safari, this toolbar only collapses when a genuine scroll gesture happens on the
// document — this whole page is deliberately made unscrollable
// (`.share-viewer-shell{overflow:hidden}`, `overscroll-behavior:none` to block pull-to-refresh), so
// the toolbar never gets the chance to collapse on its own until the user happens to drag on an area
// Google Maps doesn't capture for its own pan/zoom (matches what the user reported earlier — dragging
// the white strip made it go away).
//
// Fixed with the classic mobile-web technique (`window.scrollTo(0, 1)` to nudge a 1px scroll) —
// fired ourselves instead of waiting for the user's finger, a real scroll of the native UIScrollView
// backing the WKWebView (not a synthetic event), so it can trigger WebKit's toolbar-collapse mechanism
// the same way a manual drag does (not verified on a real iOS device).
//
// `body{height:100%}` (public/css/index.css) is a % against `<html>`, which nothing in this app ever
// sets a height on (always `auto`) — so body's % never resolves to a real length. `calc(100% + 1px)`
// on body alone therefore does nothing. `<html>` needs a real height first (a root element's % height
// resolves against the initial containing block = the viewport, per spec, regardless of any ancestor)
// before body's % can resolve to anything real.
//
// win/doc are injected (default to the globals) so this is testable with a fake window in jsdom
// without needing real timers or a real browser (see src/__tests__/scrollNudge.test.js).
export function startScrollNudge(win = window, doc = document) {
  const originalHtmlHeight = doc.documentElement.style.height
  const originalBodyHeight = doc.body.style.height
  doc.documentElement.style.height = "100%"
  doc.body.style.height = "calc(100% + 1px)"

  // พอ document scroll ได้จริง pull-to-refresh ก็กลับมาเอื้อมถึงตอนอยู่บนสุด (สวายป์ลงแล้วหน้า reload
  // ทั้งหน้า) — `.share-viewer-shell` มี `overscroll-behavior: none` อยู่แล้วแต่**ไม่มีผลกับ document**
  // เพราะเป็น div ลูก ไม่ใช่ html/body (มีแต่ 2 ตัวนั้นที่ propagate ค่านี้ไปถึง viewport ตาม spec) —
  // ต้องตั้งที่ html+body คู่กัน แล้วคืนค่าเดิมตอน stop() (code review 2026-09-07)
  // once the document really is scrollable, pull-to-refresh is back within reach at the top (a downward
  // swipe reloads the whole page) — `.share-viewer-shell` already has `overscroll-behavior: none` but
  // it **does nothing for the document**, being a child div rather than html/body (only those two
  // propagate this to the viewport, per spec) — so set it on both html and body, and restore on stop()
  // (code review 2026-09-07)
  const originalHtmlOverscroll = doc.documentElement.style.overscrollBehavior
  const originalBodyOverscroll = doc.body.style.overscrollBehavior
  doc.documentElement.style.overscrollBehavior = "none"
  doc.body.style.overscrollBehavior = "none"

  // ผู้ชมเริ่มเลื่อนหน้าเองแล้ว = เลิก nudge — retry ที่ 300/800ms ยิง `scrollTo(0,1)` ทับจะดึงหน้ากลับขึ้น
  // ไปเอง และทำให้ toolbar ที่กำลังจะหุบกางกลับมา (แย่กว่าไม่ทำอะไรเลย) · shell สูงเท่า large viewport
  // (`100lvh`) หน้าจึง scroll ได้จริงตั้งแต่โหลด ไม่ใช่กรณีสมมติ (code review 2026-09-07)
  // the viewer already started scrolling = stop nudging — a 300/800ms retry firing `scrollTo(0, 1)`
  // over that would snap the page back and re-expand the very toolbar the nudge is collapsing (worse
  // than doing nothing). The shell is as tall as the large viewport (`100lvh`), so the page really is
  // scrollable from load — not a hypothetical (code review 2026-09-07)
  const nudge = () => {
    if ((win.scrollY || 0) > 1) return
    win.scrollTo(0, 1)
  }

  // เก็บ id ของ rAF ชั้นในแยกต่างหาก (ปิดด้วย closure) — cancel แค่ตัวนอกอย่างเดียวไม่พอ ถ้า caller เรียก
  // stop() หลัง rAF ตัวนอกยิงไปแล้วแต่ก่อนตัวในจะยิง จะยังมี nudge() ค้างทำงานอยู่
  // track the inner rAF's id separately (via closure) — cancelling only the outer id isn't enough; if
  // the caller invokes stop() after the outer rAF fires but before the inner one does, a stray nudge()
  // would still be pending
  let innerRaf = null
  const outerRaf = win.requestAnimationFrame(() => {
    innerRaf = win.requestAnimationFrame(nudge)
  })
  const retryTimers = [300, 800].map((delay) => win.setTimeout(nudge, delay))

  return function stopScrollNudge() {
    doc.documentElement.style.height = originalHtmlHeight
    doc.body.style.height = originalBodyHeight
    doc.documentElement.style.overscrollBehavior = originalHtmlOverscroll
    doc.body.style.overscrollBehavior = originalBodyOverscroll
    win.cancelAnimationFrame(outerRaf)
    if (innerRaf !== null) win.cancelAnimationFrame(innerRaf)
    retryTimers.forEach((id) => win.clearTimeout(id))
  }
}
